import React, { useEffect, useState } from "react";
import { View, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from "react-native";
import { useRoute, useNavigation } from "@react-navigation/native";
import type { NavigationProp } from "@react-navigation/native";
import { MotiView } from "moti";
import {
  ArrowLeft,
  ArrowsClockwise,
  Download,
  CheckCircle,
  Warning,
  Lightbulb,
  Star,
  BookOpen,
  ClipboardText,
  CaretDown,
  CaretUp,
  Check,
} from "@phosphor-icons/react";

import { useGetLatestCourseEvaluationQuery, useEvaluateCourseMutation } from "../../graphql/generated/graphql";

import AdminLayout from "../../layouts/AdminLayout.web";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import type { RootStackParamList } from "../../navigation/AppNavigator.web";

// Badge component for effectiveness ratings
const EffectivenessBadge = ({ rating }: { rating: string }) => {
  const getBadgeStyle = () => {
    switch (rating?.toLowerCase()) {
      case "excellent":
        return { bg: "#ECFDF3", text: "#027A48", border: "#A6F4C5" };
      case "good":
        return { bg: "#EEF4FF", text: "#3538CD", border: "#C7D7FE" };
      case "fair":
        return { bg: "#FFFAEB", text: "#B54708", border: "#FEDF89" };
      case "poor":
        return { bg: "#FEF3F2", text: "#B42318", border: "#FECDCA" };
      default:
        return { bg: "#F2F4F7", text: "#344054", border: "#E4E7EC" };
    }
  };

  const style = getBadgeStyle();

  return (
    <View style={[styles.effectivenessBadge, { backgroundColor: style.bg, borderColor: style.border }]}>
      <CustomText useTranslationText={false} text={rating} size="xs" weight="medium" style={{ color: style.text }} />
    </View>
  );
};

// Collapsible assignment card
const AssignmentAccordion = ({ analysis }: { analysis: any }) => {
  const [expanded, setExpanded] = useState(false);

  const getIcon = () => {
    switch (analysis.effectiveness?.toLowerCase()) {
      case "excellent":
        return <Star size={20} color="#027A48" weight="fill" />;
      case "good":
        return <CheckCircle size={20} color="#3538CD" weight="fill" />;
      case "fair":
        return <Warning size={20} color="#B54708" weight="fill" />;
      default:
        return <CheckCircle size={20} color="#667085" />;
    }
  };

  return (
    <TouchableOpacity style={styles.assignmentAccordion} onPress={() => setExpanded(!expanded)} activeOpacity={0.7}>
      <View style={styles.assignmentAccordionHeader}>
        {getIcon()}
        <View style={styles.assignmentAccordionContent}>
          <View style={styles.assignmentTitleRow}>
            <CustomText
              useTranslationText={false}
              text={analysis.title}
              size="s"
              weight="semiBold"
              style={styles.assignmentTitle}
            />
            <EffectivenessBadge rating={analysis.effectiveness} />
          </View>
          <CustomText
            useTranslationText={false}
            text={analysis.feedback || analysis.understandingGauge}
            size="s"
            style={styles.assignmentDescription}
            numberOfLines={expanded ? undefined : 2}
          />
        </View>
        {expanded ? <CaretUp size={20} color="#98A2B3" /> : <CaretDown size={20} color="#98A2B3" />}
      </View>

      {expanded && analysis.suggestions && analysis.suggestions.length > 0 && (
        <View style={styles.assignmentSuggestions}>
          <CustomText
            useTranslationText={false}
            text="Suggestions"
            size="xs"
            weight="bold"
            style={styles.suggestionsLabel}
          />
          {analysis.suggestions.map((s: string, idx: number) => (
            <View key={idx} style={styles.suggestionItem}>
              <Lightbulb size={14} color="#F79009" />
              <CustomText useTranslationText={false} text={s} size="xs" style={styles.suggestionText} />
            </View>
          ))}
        </View>
      )}
    </TouchableOpacity>
  );
};

// Recommendation item with checkbox
const RecommendationItem = ({ text, type }: { text: string; type: "critical" | "recommended" | "enhancement" }) => {
  const [checked, setChecked] = useState(false);

  const getTypeColor = () => {
    switch (type) {
      case "critical":
        return "#B42318";
      case "recommended":
        return "#B54708";
      case "enhancement":
        return "#3538CD";
    }
  };

  return (
    <View style={styles.recommendationItem}>
      <TouchableOpacity
        style={[styles.checkbox, checked && { backgroundColor: getTypeColor(), borderColor: getTypeColor() }]}
        onPress={() => setChecked(!checked)}>
        {checked && <Check size={12} color="#fff" weight="bold" />}
      </TouchableOpacity>
      <CustomText
        useTranslationText={false}
        text={text}
        size="s"
        style={[styles.recommendationText, { color: getTypeColor() }]}
      />
    </View>
  );
};

function CourseEvaluationDetailView() {
  const route = useRoute();
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { courseId } = route.params as { courseId: string };

  const { data, loading, refetch } = useGetLatestCourseEvaluationQuery({
    variables: { courseId },
    fetchPolicy: "network-only",
  });

  const evaluation = data?.getLatestCourseEvaluation;
  const [evaluateCourse, { loading: evaluating }] = useEvaluateCourseMutation();
  const isPending = evaluation?.status === "pending";

  const handleEvaluation = async () => {
    try {
      await evaluateCourse({ variables: { courseId } });
      refetch();
    } catch (err) {
      console.log("Error starting evaluation", err);
    }
  };

  useEffect(() => {
    if (!evaluation || evaluation.status !== "pending") return;
    const interval = setInterval(() => {
      refetch();
    }, 2000);
    return () => clearInterval(interval);
  }, [evaluation, refetch]);

  const curriculum = evaluation?.curriculumEvaluationJson ? JSON.parse(evaluation.curriculumEvaluationJson) : null;
  const assignmentEvaluation = evaluation?.assignmentEvaluationJson
    ? JSON.parse(evaluation.assignmentEvaluationJson)
    : null;

  const hasCompletedEvaluation = evaluation?.status === "completed" && curriculum && assignmentEvaluation;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Group recommendations by severity
  const getRecommendations = () => {
    const recs = evaluation?.teacherRecommendations || [];
    // For now, distribute recommendations across categories
    const critical = recs.slice(0, Math.ceil(recs.length / 3));
    const recommended = recs.slice(Math.ceil(recs.length / 3), Math.ceil((recs.length * 2) / 3));
    const enhancement = recs.slice(Math.ceil((recs.length * 2) / 3));
    return { critical, recommended, enhancement };
  };

  const recommendations = getRecommendations();
  const totalRecommendations =
    recommendations.critical.length + recommendations.recommended.length + recommendations.enhancement.length;

  // Get assignment stats
  const getAssignmentStats = () => {
    const analysis = assignmentEvaluation?.assignmentAnalysis || [];
    const stats = { excellent: 0, good: 0, fair: 0, poor: 0 };
    analysis.forEach((a: any) => {
      const eff = a.effectiveness?.toLowerCase();
      if (eff === "excellent") stats.excellent++;
      else if (eff === "good") stats.good++;
      else if (eff === "fair") stats.fair++;
      else if (eff === "poor") stats.poor++;
    });
    return { total: analysis.length, ...stats };
  };

  const assignmentStats = getAssignmentStats();

  return (
    <AdminLayout withPadding={false}>
      <ScrollView style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          {/* Back button */}
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <ArrowLeft size={16} color="#667085" />
            <CustomText
              useTranslationText={false}
              text="Back to Course Evaluator"
              size="s"
              weight="medium"
              style={styles.backButtonText}
            />
          </TouchableOpacity>

          <CustomSpacing type="vertical" size="m" />

          {/* Header */}
          <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
            <View style={styles.headerSection}>
              <View style={styles.headerLeft}>
                <CustomText
                  useTranslationText={false}
                  text={evaluation?.courseHasEvaluation?.name || "Course Evaluation"}
                  size="l"
                  textType="display"
                  weight="ultraBold"
                  style={styles.pageTitle}
                />
                <CustomText
                  useTranslationText={false}
                  text={evaluation?.createdAt ? `Evaluated on ${formatDate(evaluation.createdAt)}` : ""}
                  size="s"
                  style={styles.headerMeta}
                />
                <CustomSpacing type="vertical" size="s" />
                <CustomText
                  useTranslationText={false}
                  text={`Course ID: ${courseId}`}
                  size="s"
                  style={styles.headerMeta}
                />
              </View>
              <View style={styles.headerActions}>
                <TouchableOpacity style={styles.secondaryButton}>
                  <Download size={16} color="#344054" />
                  <CustomText
                    useTranslationText={false}
                    text="Export"
                    size="s"
                    weight="medium"
                    style={styles.buttonText}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.primaryButton}
                  onPress={handleEvaluation}
                  disabled={evaluating || isPending}>
                  {evaluating || isPending ? (
                    <ActivityIndicator color="#fff" size="small" />
                  ) : (
                    <>
                      <ArrowsClockwise size={16} color="#fff" />
                      <CustomText
                        useTranslationText={false}
                        text={evaluation ? "Re-evaluate" : "Evaluate"}
                        size="s"
                        weight="medium"
                        style={styles.primaryButtonText}
                      />
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          </MotiView>

          <CustomSpacing type="vertical" size="l" />

          {/* Loading state */}
          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#7F56D9" />
              <CustomSpacing type="vertical" size="s" />
              <CustomText useTranslationText={false} text="Loading evaluation..." size="m" style={styles.loadingText} />
            </View>
          )}

          {/* Pending state */}
          {isPending && (
            <View style={styles.pendingCard}>
              <ActivityIndicator size="small" color="#7F56D9" />
              <CustomText
                useTranslationText={false}
                text="Evaluation is being generated..."
                size="m"
                weight="medium"
                style={styles.pendingText}
              />
            </View>
          )}

          {/* No evaluation state */}
          {!loading && !evaluation && (
            <View style={styles.emptyCard}>
              <ClipboardText size={48} color="#D0D5DD" />
              <CustomSpacing type="vertical" size="m" />
              <CustomText
                useTranslationText={false}
                text="No evaluation yet"
                size="m"
                weight="semiBold"
                style={styles.emptyTitle}
              />
              <CustomText
                useTranslationText={false}
                text="Click the Evaluate button to generate an AI-powered course evaluation"
                size="s"
                style={styles.emptyDescription}
              />
            </View>
          )}

          {/* Completed evaluation */}
          {hasCompletedEvaluation && (
            <>
              {/* Overall Summary Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeader}>
                    <CheckCircle size={24} color="#7F56D9" weight="duotone" />
                    <CustomText
                      useTranslationText={false}
                      text="Overall Summary"
                      size="m"
                      weight="bold"
                      style={styles.cardTitle}
                    />
                  </View>

                  {/* Effectiveness badges */}
                  <View style={styles.badgeRow}>
                    <View style={styles.badgeGroup}>
                      <CustomText useTranslationText={false} text="Curriculum:" size="s" style={styles.badgeLabel} />
                      <EffectivenessBadge rating={curriculum.effectiveness || "N/A"} />
                    </View>
                    <View style={styles.badgeGroup}>
                      <CustomText useTranslationText={false} text="Assignments:" size="s" style={styles.badgeLabel} />
                      <EffectivenessBadge rating={assignmentEvaluation.effectiveness || "N/A"} />
                    </View>
                  </View>

                  {/* Recommendations section */}
                  {/* {totalRecommendations > 0 && (
                    <View style={styles.recommendationsSection}>
                      <View style={styles.recommendationsHeader}>
                        <View style={styles.recommendationsTitle}>
                          <Lightbulb size={16} color="#F79009" />
                          <CustomText
                            useTranslationText={false}
                            text="Recommendations"
                            size="s"
                            weight="semiBold"
                            style={styles.recommendationsTitleText}
                          />
                        </View>
                        <CustomText
                          useTranslationText={false}
                          text={`0 of ${totalRecommendations} addressed`}
                          size="xs"
                          style={styles.recommendationsCount}
                        />
                      </View>

                      {/* Critical recommendations */}
                  {/* {recommendations.critical.length > 0 && (
                        <View style={[styles.recommendationGroup, styles.criticalGroup]}>
                          <View style={styles.recommendationGroupHeader}>
                            <Warning size={16} color="#B42318" />
                            <CustomText
                              useTranslationText={false}
                              text={`Critical (${recommendations.critical.length})`}
                              size="s"
                              weight="semiBold"
                              style={{ color: "#B42318" }}
                            />
                          </View>
                          {recommendations.critical.map((rec, idx) => (
                            <RecommendationItem key={idx} text={rec} type="critical" />
                          ))}
                        </View>
                      )} */}

                  {/* Recommended */}
                  {/* {recommendations.recommended.length > 0 && (
                        <View style={[styles.recommendationGroup, styles.recommendedGroup]}>
                          <View style={styles.recommendationGroupHeader}>
                            <Lightbulb size={16} color="#B54708" />
                            <CustomText
                              useTranslationText={false}
                              text={`Recommended (${recommendations.recommended.length})`}
                              size="s"
                              weight="semiBold"
                              style={{ color: "#B54708" }}
                            />
                          </View>
                          {recommendations.recommended.map((rec, idx) => (
                            <RecommendationItem key={idx} text={rec} type="recommended" />
                          ))}
                        </View>
                      )} */}

                  {/* Enhancement */}
                  {/* {recommendations.enhancement.length > 0 && (
                        <View style={[styles.recommendationGroup, styles.enhancementGroup]}>
                          <View style={styles.recommendationGroupHeader}>
                            <Star size={16} color="#3538CD" />
                            <CustomText
                              useTranslationText={false}
                              text={`Enhancement (${recommendations.enhancement.length})`}
                              size="s"
                              weight="semiBold"
                              style={{ color: "#3538CD" }}
                            />
                          </View>
                          {recommendations.enhancement.map((rec, idx) => (
                            <RecommendationItem key={idx} text={rec} type="enhancement" />
                          ))}
                        </View>
                      )}
                    </View>
                  )} */}
                </View>
              </MotiView>

              {/* Curriculum Evaluation Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeaderWithBadge}>
                    <View style={styles.cardHeaderLeft}>
                      <BookOpen size={24} color="#7F56D9" weight="duotone" />
                      <View>
                        <CustomText
                          useTranslationText={false}
                          text="Curriculum Evaluation"
                          size="m"
                          weight="bold"
                          style={styles.cardTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Analysis of curriculum completeness and essential concept coverage"
                          size="xs"
                          style={styles.cardSubtitle}
                        />
                      </View>
                    </View>
                    <EffectivenessBadge rating={curriculum.effectiveness || "N/A"} />
                  </View>

                  {/* Concept Coverage */}
                  {curriculum.conceptCoverage && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <BookOpen size={16} color="#667085" />
                        <CustomText
                          useTranslationText={false}
                          text="Concept Coverage"
                          size="s"
                          weight="semiBold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      <CustomText
                        useTranslationText={false}
                        text={curriculum.conceptCoverage}
                        size="s"
                        style={styles.subsectionText}
                      />
                    </View>
                  )}

                  {/* Strengths */}
                  {curriculum.strengths && curriculum.strengths.length > 0 && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <CheckCircle size={16} color="#12B76A" />
                        <CustomText
                          useTranslationText={false}
                          text="Strengths"
                          size="s"
                          weight="semiBold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      {curriculum.strengths.map((item: string, idx: number) => (
                        <View key={idx} style={styles.strengthItem}>
                          <Check size={14} color="#12B76A" />
                          <CustomText useTranslationText={false} text={item} size="s" style={styles.strengthText} />
                        </View>
                      ))}
                    </View>
                  )}

                  {/* Improvement Suggestions */}
                  {curriculum.suggestions && curriculum.suggestions.length > 0 && (
                    <View style={styles.subsection}>
                      <View style={styles.subsectionHeader}>
                        <Lightbulb size={16} color="#F79009" />
                        <CustomText
                          useTranslationText={false}
                          text="Improvement Suggestions"
                          size="s"
                          weight="semiBold"
                          style={styles.subsectionTitle}
                        />
                      </View>
                      {curriculum.suggestions.map((item: string, idx: number) => (
                        <View key={idx} style={styles.suggestionListItem}>
                          <View style={styles.suggestionNumber}>
                            <CustomText
                              useTranslationText={false}
                              text={String(idx + 1)}
                              size="xs"
                              weight="bold"
                              style={styles.suggestionNumberText}
                            />
                          </View>
                          <CustomText
                            useTranslationText={false}
                            text={item}
                            size="s"
                            style={styles.suggestionItemText}
                          />
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              </MotiView>

              {/* Assignment Evaluation Card */}
              <MotiView from={{ opacity: 0, translateY: 20 }} animate={{ opacity: 1, translateY: 0 }}>
                <View style={styles.card}>
                  <View style={styles.cardHeaderWithBadge}>
                    <View style={styles.cardHeaderLeft}>
                      <ClipboardText size={24} color="#7F56D9" weight="duotone" />
                      <View>
                        <CustomText
                          useTranslationText={false}
                          text="Assignment Evaluation"
                          size="m"
                          weight="bold"
                          style={styles.cardTitle}
                        />
                        <CustomText
                          useTranslationText={false}
                          text="Evaluation of how well assignments gauge understanding and cover concepts"
                          size="xs"
                          style={styles.cardSubtitle}
                        />
                      </View>
                    </View>
                    <EffectivenessBadge rating={assignmentEvaluation.effectiveness || "N/A"} />
                  </View>

                  {/* Assignment stats */}
                  <View style={styles.assignmentStatsRow}>
                    <CustomText
                      useTranslationText={false}
                      text={`${assignmentStats.total} assignments evaluated`}
                      size="s"
                      weight="medium"
                      style={styles.assignmentStatsLabel}
                    />
                    <View style={styles.statsBreakdown}>
                      {assignmentStats.good > 0 && (
                        <CustomText
                          useTranslationText={false}
                          text={`${assignmentStats.good} Good`}
                          size="xs"
                          weight="medium"
                          style={{ color: "#3538CD" }}
                        />
                      )}
                      {assignmentStats.fair > 0 && (
                        <CustomText
                          useTranslationText={false}
                          text={`${assignmentStats.fair} Fair`}
                          size="xs"
                          weight="medium"
                          style={{ color: "#B54708" }}
                        />
                      )}
                      {assignmentStats.excellent > 0 && (
                        <CustomText
                          useTranslationText={false}
                          text={`${assignmentStats.excellent} Excellent`}
                          size="xs"
                          weight="medium"
                          style={{ color: "#027A48" }}
                        />
                      )}
                    </View>
                  </View>

                  {assignmentEvaluation.conceptCoverageByAssignments && (
                    <CustomText
                      useTranslationText={false}
                      text={assignmentEvaluation.conceptCoverageByAssignments}
                      size="s"
                      style={styles.assignmentCoverageText}
                    />
                  )}

                  {/* Assignment list */}
                  {assignmentEvaluation.assignmentAnalysis && assignmentEvaluation.assignmentAnalysis.length > 0 && (
                    <View style={styles.assignmentList}>
                      {assignmentEvaluation.assignmentAnalysis.map((analysis: any) => (
                        <AssignmentAccordion key={analysis.assignmentId || analysis.title} analysis={analysis} />
                      ))}
                    </View>
                  )}
                </View>
              </MotiView>
            </>
          )}
        </View>
      </ScrollView>
    </AdminLayout>
  );
}

const styles = StyleSheet.create({
  pageBackground: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  contentContainer: {
    padding: 32,
    maxWidth: 900,
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  backButtonText: {
    color: "#667085",
  },
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  pageTitle: {
    color: "#101828",
    fontSize: 24,
    lineHeight: 32,
  },
  headerMeta: {
    color: "#667085",
    marginTop: 4,
  },
  headerActions: {
    flexDirection: "row",
    gap: 12,
  },
  secondaryButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: "#fff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D5DD",
  },
  buttonText: {
    color: "#344054",
  },
  primaryButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: "#7F56D9",
    borderRadius: 8,
    minWidth: 120,
    justifyContent: "center",
  },
  primaryButtonText: {
    color: "#fff",
  },
  loadingContainer: {
    padding: 60,
    alignItems: "center",
    justifyContent: "center",
  },
  loadingText: {
    color: "#667085",
  },
  pendingCard: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 20,
    backgroundColor: "#F9F5FF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E9D7FE",
  },
  pendingText: {
    color: "#7F56D9",
  },
  emptyCard: {
    padding: 60,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    borderStyle: "dashed",
  },
  emptyTitle: {
    color: "#344054",
  },
  emptyDescription: {
    color: "#667085",
    textAlign: "center",
    marginTop: 4,
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    padding: 24,
    marginBottom: 20,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    marginBottom: 16,
  },
  cardHeaderWithBadge: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  cardHeaderLeft: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    flex: 1,
  },
  cardTitle: {
    color: "#101828",
  },
  cardSubtitle: {
    color: "#667085",
    marginTop: 2,
  },
  badgeRow: {
    flexDirection: "row",
    gap: 24,
    marginBottom: 20,
  },
  badgeGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  badgeLabel: {
    color: "#667085",
  },
  effectivenessBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
  },
  recommendationsSection: {
    borderTopWidth: 1,
    borderTopColor: "#E4E7EC",
    paddingTop: 16,
  },
  recommendationsHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  recommendationsTitle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  recommendationsTitleText: {
    color: "#344054",
  },
  recommendationsCount: {
    color: "#667085",
  },
  recommendationGroup: {
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  criticalGroup: {
    backgroundColor: "#FEF3F2",
  },
  recommendedGroup: {
    backgroundColor: "#FFFAEB",
  },
  enhancementGroup: {
    backgroundColor: "#EEF4FF",
  },
  recommendationGroupHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  recommendationItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 8,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D0D5DD",
    backgroundColor: "#fff",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 2,
  },
  recommendationText: {
    flex: 1,
    lineHeight: 20,
  },
  subsection: {
    marginBottom: 20,
  },
  subsectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
  },
  subsectionTitle: {
    color: "#344054",
  },
  subsectionText: {
    color: "#475467",
    lineHeight: 22,
  },
  strengthItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 8,
  },
  strengthText: {
    color: "#475467",
    flex: 1,
    lineHeight: 20,
  },
  suggestionListItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 10,
  },
  suggestionNumber: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#F79009",
    alignItems: "center",
    justifyContent: "center",
  },
  suggestionNumberText: {
    color: "#fff",
  },
  suggestionItemText: {
    color: "#475467",
    flex: 1,
    lineHeight: 20,
  },
  assignmentStatsRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  assignmentStatsLabel: {
    color: "#344054",
  },
  statsBreakdown: {
    flexDirection: "row",
    gap: 16,
  },
  assignmentCoverageText: {
    color: "#475467",
    lineHeight: 22,
    marginBottom: 20,
  },
  assignmentList: {
    gap: 12,
  },
  assignmentAccordion: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E4E7EC",
    padding: 16,
  },
  assignmentAccordionHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  assignmentAccordionContent: {
    flex: 1,
  },
  assignmentTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 4,
  },
  assignmentTitle: {
    color: "#101828",
    flex: 1,
  },
  assignmentDescription: {
    color: "#475467",
    lineHeight: 20,
  },
  assignmentSuggestions: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: "#E4E7EC",
  },
  suggestionsLabel: {
    color: "#667085",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  suggestionItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 6,
  },
  suggestionText: {
    color: "#475467",
    flex: 1,
    lineHeight: 18,
  },
});

export default CourseEvaluationDetailView;
